import React from 'react';
import { Link } from 'react-router-dom';
import TestEdildiBadge from 'components/TestEdildiBadge';
import ListingContactActions from 'components/Listing/ListingContactActions';
import type { ListingDetailProduct } from 'api/utils/listingDetailMapper';
import {
  formatListingLocationLabel,
  hasListingLocationArea,
} from 'api/utils/listingDetailMapper';
import type { ListingContactMethodValue } from 'constants/listingContactMethod';
import './ListingDetailSidebar.css';

export interface ListingDetailSidebarProps {
  product: ListingDetailProduct;
  isListingOwner: boolean;
  isUserFavorite?: boolean;
  contactSellerLoading?: boolean;
  onDirectMessage: () => void;
  onToggleFavorite?: () => void;
  onShare: () => void;
  shareButtonRef?: React.RefObject<HTMLButtonElement | null>;
  /** When set, opens report modal via Bootstrap data attributes. */
  reportModalTargetId?: string;
  ownerActions?: React.ReactNode;
  buyerExtraActions?: React.ReactNode;
  showRejectionAlert?: boolean;
}

function sellerProfilePath(product: ListingDetailProduct): string | null {
  if (!product.sellerId) return null;
  return product.seller.accountType === 'Corporate'
    ? `/satici-profil-kurumsal/${product.sellerId}`
    : `/satici-profil/${product.sellerId}`;
}

function sellerTypeLabel(accountType?: 'Personal' | 'Corporate'): string {
  if (accountType === 'Corporate') return 'Kurumsal satıcı';
  if (accountType === 'Personal') return 'Bireysel satıcı';
  return 'Satıcı profili';
}

const ListingDetailSidebar: React.FC<ListingDetailSidebarProps> = ({
  product,
  isListingOwner,
  isUserFavorite = false,
  contactSellerLoading = false,
  onDirectMessage,
  onToggleFavorite,
  onShare,
  shareButtonRef,
  reportModalTargetId = '#complaintModal',
  ownerActions,
  buyerExtraActions,
  showRejectionAlert = false,
}) => {
  const profilePath = sellerProfilePath(product);
  const locationLabel = formatListingLocationLabel(product.location);
  const listingCode = product.listingCode ?? product.id;
  const conditionLabel = product.conditionName ?? '—';
  const viewLabel =
    product.viewCount != null ? String(product.viewCount) : '—';

  const avatarClass =
    product.seller.accountType === 'Corporate'
      ? 'sb-listing-detail-sidebar__seller-avatar sb-listing-detail-sidebar__seller-avatar--corporate rounded'
      : 'sb-listing-detail-sidebar__seller-avatar rounded-circle';

  return (
    <div className="card sb-listing-detail-sidebar mb-4">
      <div className="card-body">
        <div className="sb-listing-detail-sidebar__seller">
          <img
            alt={product.seller.accountType === 'Corporate' ? 'Şirket logosu' : 'Satıcı'}
            className={avatarClass}
            height={48}
            src={product.seller.avatar}
            width={48}
          />
          <div className="min-w-0">
            <span className="sb-listing-detail-sidebar__seller-label">Satıcı</span>
            {profilePath ? (
              <Link className="sb-listing-detail-sidebar__seller-type-link" to={profilePath}>
                {sellerTypeLabel(product.seller.accountType)}
              </Link>
            ) : (
              <span className="sb-listing-detail-sidebar__seller-type-link">{product.seller.name}</span>
            )}
          </div>
        </div>

        <h1 className="sb-listing-detail-sidebar__title">{product.title}</h1>

        {product.isTested && (
          <div className="mb-2">
            <TestEdildiBadge />
          </div>
        )}

        <div className="sb-listing-detail-sidebar__price">
          {product.price.toLocaleString('tr-TR')} ₺
        </div>

        {showRejectionAlert && product.rejectionReason && (
          <div className="alert alert-danger mb-3" role="alert">
            <i className="fas fa-times-circle me-2" aria-hidden />
            <strong>İlan reddedildi.</strong> {product.rejectionReason}
          </div>
        )}

        <div className="sb-listing-detail-sidebar__meta">
          {hasListingLocationArea(product.location) && (
            <div>
              <i className="fas fa-map-marker-alt me-2" aria-hidden />
              {locationLabel}
            </div>
          )}
          {product.seller.accountType && (
            <div>
              <i className="fas fa-user me-2" aria-hidden />
              {sellerTypeLabel(product.seller.accountType)}
            </div>
          )}
        </div>

        <div className="sb-listing-detail-sidebar__stats" aria-label="İlan özeti">
          <div className="sb-listing-detail-sidebar__stat">
            <span className="sb-listing-detail-sidebar__stat-label">Durum</span>
            <span className="sb-listing-detail-sidebar__stat-value">{conditionLabel}</span>
          </div>
          <div className="sb-listing-detail-sidebar__stat">
            <span className="sb-listing-detail-sidebar__stat-label">Görüntülenme</span>
            <span className="sb-listing-detail-sidebar__stat-value">{viewLabel}</span>
          </div>
          <div className="sb-listing-detail-sidebar__stat">
            <span className="sb-listing-detail-sidebar__stat-label">İlan kodu</span>
            <span className="sb-listing-detail-sidebar__stat-value">{listingCode}</span>
          </div>
        </div>

        {isListingOwner ? (
          ownerActions ? (
            <div className="sb-listing-detail-sidebar__owner-actions">{ownerActions}</div>
          ) : null
        ) : (
          <>
            {contactSellerLoading ? (
              <div className="text-center py-3">
                <span className="spinner-border spinner-border-sm text-primary" role="status" />
              </div>
            ) : (
              <ListingContactActions
                listingCode={listingCode}
                contactMethods={(product.contactMethods ?? []) as ListingContactMethodValue[]}
                sellerPhoneNumber={product.sellerPhoneNumber}
                sellerWhatsappPhoneDigits={product.sellerWhatsappPhoneDigits}
                isListingOwner={false}
                onDirectMessage={onDirectMessage}
              />
            )}
            <div className="sb-listing-detail-sidebar__utility-row">
              {onToggleFavorite && (
                <button
                  type="button"
                  className={`sb-listing-detail-sidebar__utility-btn ${
                    isUserFavorite ? 'sb-listing-detail-sidebar__utility-btn--favorite-active' : ''
                  }`.trim()}
                  onClick={onToggleFavorite}
                  aria-pressed={isUserFavorite}
                  aria-label={isUserFavorite ? 'Favorilerden çıkar' : 'Favoriye ekle'}
                >
                  <i className={`${isUserFavorite ? 'fas' : 'far'} fa-heart`} aria-hidden />
                  Favoriye ekle
                </button>
              )}
              <button
                type="button"
                className="sb-listing-detail-sidebar__utility-btn"
                onClick={onShare}
                ref={shareButtonRef as React.RefObject<HTMLButtonElement>}
              >
                <i className="fas fa-share-alt" aria-hidden />
                Paylaş
              </button>
              <button
                type="button"
                className="sb-listing-detail-sidebar__utility-btn sb-listing-detail-sidebar__utility-btn--report"
                data-bs-target={reportModalTargetId}
                data-bs-toggle="modal"
              >
                <i className="fas fa-exclamation-triangle" aria-hidden />
                Şikayet Et
              </button>
            </div>
            {buyerExtraActions}
          </>
        )}
      </div>
    </div>
  );
};

export default ListingDetailSidebar;
